import ModalComponent from "components/common/Modal/Modal";
import ScenarioConfigContext from "Context/ScenarioConfigContext";
import UserContext from "Context/UserProvider";
import UseInterval from "custom_hook/useInterval";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { 
    Backdrop, 
    CircularProgress, 
    Box, 
    Typography, 
    LinearProgress,
    Button,
    Grid
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import DashboardCard from 'components/dashboard/common/DashboardCard';
import toast from "react-hot-toast";
import { useApiService } from "../../../../services/ApiService/ApiService";

function RunPage(props: any) {
    const { userInfo, setUserInfo } = useContext(UserContext);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [scenarioConfigInfo, setScenarioConfigInfo] = useContext(ScenarioConfigContext) as any;
    const [runningScenario, setRunningScenario] = useState(scenarioConfigInfo.runScenario);
    const [draftScenario, setDraftScenario] = useState(scenarioConfigInfo.draftScenario);
    const [statusPolling, setStatusPolling] = useState(runningScenario !== undefined);
    const [running, setRunning] = useState(false);
    const { fetchWithAuth } = useApiService();
    const navigate = useNavigate();

    async function fetchDraft() {
        setLoading(true);
        try {
            const response = await fetchWithAuth('/DragDrop/fleet/scenario/draft', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch draft scenario');
            }

            const res_json = await response.json();

            if (res_json.runningScenario !== null) {
                setRunningScenario({ 
                    ...res_json.runningScenario, 
                    percentageComplete: res_json.runningScenario.percentageComplete.toFixed(2) 
                });
                setDraftScenario(res_json.draftScenario);
                setStatusPolling(true);
                return;
            }

            setScenarioConfigInfo((prev: any) => ({
                ...prev,
                draftScenario: res_json.draftScenario,
                runningScenario: null,
            }));
            setDraftScenario(res_json.draftScenario);
        } catch (error) {
            console.error('Error fetching draft scenario:', error);
            setIsError(true);
            toast.error('Failed to fetch draft scenario');
        } finally {
            setLoading(false);
        }
    }

    async function runScenario() {
        setRunning(true);
        try {
            const response = await fetchWithAuth('/DragDrop/fleet/scenario/run', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid,
                    fleetScenarioUuid: draftScenario.uuid
                })
            });

            const res_json = await response.json();

            if (!response.ok) {
                switch (res_json.code) {
                    case "FLEET_SCENARIO_NO_ELECTRIC_VEHICLES_SELECTED":
                        setErrorMessage("No electric vehicles have been selected.");
                        break;
                    case "FLEET_SCENARIO_NO_ELECTRIC_VEHICLE_COMPARISONS":
                        setErrorMessage(`No suitable electric vehicles selected for ${res_json.fields.map((e: any) => e + ",")}.`);
                        break;
                    case "FLEET_SCENARIO_NO_DWELLINGS_FOR_INFRASTRUCTURE_ACTUAL":
                        setErrorMessage("No locations have been selected although 'Actual Locations' was selected.");
                        break;
                    case "FLEET_SCENARIO_NO_ICE_VEHICLES_SELECTED":
                        setErrorMessage("No ICE vehicle was selected.");
                        break;
                    default:
                        setErrorMessage("There was an error during running scenario");
                        break;
                }
                setIsError(true);
                return;
            }

            setRunningScenario(draftScenario);
            await fetchDraft();
            setStatusPolling(true);
        } catch (error) {
            console.error('Error running scenario:', error);
            setErrorMessage("Failed to run scenario");
            setIsError(true);
        } finally {
            setRunning(false);
        }
    }

    async function checkScenarioProgress() {
        try {
            const response = await fetchWithAuth('/DragDrop/fleet/scenario/progress', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid,
                    fleetScenarioUuid: runningScenario.uuid
                })
            });

            const res_json = await response.json();

            if (res_json.scenario.status === "IN_ERROR") {
                setErrorMessage(`An error has occurred whilst running Fleet Scenario ${runningScenario.fleetScenarioNumber}`);
                setStatusPolling(false);
                await fetchDraft();
                return;
            }

            if (res_json.percentageComplete === 1) {
                await fetchDraft();
                setStatusPolling(false);
                return;
            }

            setRunningScenario((prev: any) => ({
                ...prev,
                percentageComplete: res_json.percentageComplete,
            }));
        } catch (error) {
            console.error('Error checking scenario progress:', error);
            setErrorMessage("Failed to check scenario progress");
            setStatusPolling(false);
        }
    }

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage, {
                position: 'bottom-center',
                duration: 5000,
            });
        }
        setErrorMessage(null);
    }, [errorMessage]);

    UseInterval(checkScenarioProgress, 3000, statusPolling);

    useEffect(() => {
        if (scenarioConfigInfo.runningScenario !== null) {
            setRunningScenario({ 
                ...scenarioConfigInfo.runningScenario, 
                percentageComplete: scenarioConfigInfo.runningScenario.percentageComplete.toFixed(2) 
            });
            setStatusPolling(true);
            setLoading(false);
        } else {
            fetchDraft();
        }
    }, []);
    useEffect(() => { }, [statusPolling]);

    return (
        <>
            <Backdrop open={running} sx={{ zIndex: 1300 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            ) : statusPolling ? (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6} xl={4}>
                        <DashboardCard
                            title={`Scenario ${runningScenario.fleetScenarioNumber}`}
                            icon={<TimelineIcon />}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Created:</Typography>
                                    <Typography>{runningScenario.createdDate}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Number of Locations:</Typography>
                                    <Typography>{runningScenario.countLocations}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Number of Ice Vehicles:</Typography>
                                    <Typography>{runningScenario.countIceVehicles}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Number of Electric Vehicles:</Typography>
                                    <Typography>{runningScenario.countElectricVehicles}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                                    <Typography>In Progress</Typography>
                                </Box>
                                <Box sx={{ width: '100%', mt: 2 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={Math.round(runningScenario.percentageComplete * 100)} 
                                    />
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                        {`${Math.round(runningScenario.percentageComplete * 100)}%`}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    You can now configure your next scenario whilst this one is running
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate("/plan/scenario/configuration")}
                                    >
                                        Configure Next Scenario
                                    </Button>
                                </Box>
                            </Box>
                        </DashboardCard>
                    </Grid>
                </Grid>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6} xl={4}>
                        <DashboardCard
                            title={`Scenario ${draftScenario.fleetScenarioNumber}`}
                            icon={<TimelineIcon />}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Created:</Typography>
                                    <Typography>{draftScenario.createdDate}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Number of Locations:</Typography>
                                    <Typography>{draftScenario.countLocations}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Number of Ice Vehicles:</Typography>
                                    <Typography>{draftScenario.countIceVehicles}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Number of Electric Vehicles:</Typography>
                                    <Typography>{draftScenario.countElectricVehicles}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                                    <Typography>Draft</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={runScenario}
                                        disabled={running}
                                    >
                                        Run Scenario
                                    </Button>
                                </Box>
                            </Box>
                        </DashboardCard>
                    </Grid>
                </Grid>
            )}
        </>
    );
}

export default RunPage;
